import { useState, useEffect } from 'react'
import { useAuth } from '../auth/AuthProvider'
import {
  portalGetTenantSettings,
  portalSaveTenantSettings,
  portalListPat,
  portalCreatePat,
  portalRevokePat,
} from '../lib/portalApi'

export function SettingsPage() {
  const { user } = useAuth()
  const uid = user?.uid ?? ''

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  // Settings state
  const [p8Content, setP8Content] = useState('')
  const [issuerId, setIssuerId] = useState('')
  const [keyId, setKeyId] = useState('')
  const [serviceJson, setServiceJson] = useState('')
  // Backend không trả nội dung chứng chỉ về nữa (chúng nằm trong Secret
  // Manager), chỉ trả cờ đã-có. Ô nhập vì vậy luôn rỗng: để trống = giữ nguyên.
  const [hasP8, setHasP8] = useState(false)
  const [hasServiceJson, setHasServiceJson] = useState(false)

  // PAT state
  const [keys, setKeys] = useState<any[]>([])
  const [newKeyName, setNewKeyName] = useState('')
  const [createdKey, setCreatedKey] = useState<string | null>(null)

  useEffect(() => {
    if (!uid) return
    const fetchData = async () => {
      try {
        const idToken = await user!.getIdToken()
        const [settingsRes, keysRes] = await Promise.all([
          portalGetTenantSettings({ tenantId: uid, idToken }),
          portalListPat({ tenantId: uid, idToken })
        ])
        
        const settings = (settingsRes as any).settings || {}
        setIssuerId(settings.apple?.issuerId || '')
        setKeyId(settings.apple?.keyId || '')
        setHasP8(!!settings.apple?.hasP8Content)
        setHasServiceJson(!!settings.google?.hasServiceJson)
        
        setKeys((keysRes as any).keys || [])
      } catch (err: any) {
        setError(err.message || 'Failed to load settings')
      }
    }
    fetchData()
  }, [uid, user])

  const handleSaveStoreSettings = async () => {
    if (!uid) return
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const idToken = await user!.getIdToken()
      await portalSaveTenantSettings({
        tenantId: uid,
        idToken,
        settings: {
          apple: { p8Content, issuerId, keyId },
          google: { serviceJson }
        }
      })
      // Đã gửi đi thì xoá khỏi bộ nhớ trình duyệt ngay, đừng để nằm lại trong
      // state của form.
      if (p8Content) { setP8Content(''); setHasP8(true) }
      if (serviceJson) { setServiceJson(''); setHasServiceJson(true) }
      setMessage('Account settings saved successfully!')
    } catch (err: any) {
      setError(err.message || 'Failed to save settings')
    } finally {
      setBusy(false)
    }
  }

  const handleCreatePat = async () => {
    if (!uid || !newKeyName.trim()) return
    setBusy(true)
    setError(null)
    setCreatedKey(null)
    try {
      const idToken = await user!.getIdToken()
      const res: any = await portalCreatePat({ tenantId: uid, idToken, name: newKeyName.trim() })
      setCreatedKey(res.key.key)
      setKeys([res.key, ...keys])
      setNewKeyName('')
    } catch (err: any) {
      setError(err.message || 'Failed to create API key')
    } finally {
      setBusy(false)
    }
  }

  const handleRevoke = async (keyId: string, keyName: string) => {
    if (!window.confirm(`Revoke key "${keyName}"? This cannot be undone.`)) return
    setBusy(true)
    setError(null)
    try {
      const idToken = await user!.getIdToken()
      await portalRevokePat({ tenantId: uid, keyId, idToken })
      setKeys(prev => prev.filter(k => k.id !== keyId))
    } catch (err: any) {
      setError(err.message || 'Failed to revoke key')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-xl">
        <h2 className="font-headline-lg text-[22px] md:text-[32px] font-semibold text-on-surface leading-tight">Account Settings</h2>
        <p className="font-label-md text-[14px] text-on-surface-variant mt-1">Manage global configurations and Personal Access Tokens.</p>
      </div>

      {error && <div className="p-md bg-error-container text-on-error-container rounded-lg border border-error/20 font-body-sm mb-lg">{error}</div>}
      {message && <div className="p-md bg-[#15803d]/10 border border-[#15803d]/20 text-[#15803d] rounded-lg font-body-sm mb-lg">{message}</div>}

      <div className="space-y-lg">
        {/* Personal Access Tokens (PAT) Card */}
        <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg shadow-sm">
          <div className="mb-md">
            <h3 className="font-headline-md text-[20px] font-semibold text-on-surface">Personal Access Tokens</h3>
            <p className="font-body-sm text-[13px] text-on-surface-variant mt-1">
              Use these tokens to authenticate with the MCP Server or Developer APIs.
            </p>
          </div>

          <div className="flex gap-md mb-2 max-w-xl">
            <input 
              type="text" 
              className="flex-1 bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface"
              placeholder="Key Name (e.g., Cursor MCP)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              disabled={busy}
            />
            <button 
              className="bg-primary text-on-primary font-label-md text-[14px] px-lg py-sm rounded-lg hover:opacity-90 disabled:opacity-50 transition-all shadow-sm whitespace-nowrap"
              onClick={handleCreatePat}
              disabled={busy || !newKeyName.trim()}
            >
              Generate Key
            </button>
          </div>
          <p className="font-body-sm text-[12px] text-on-surface-variant mt-1 mb-lg">
            Token sẽ hết hạn sau 6 giờ kể từ khi tạo.
          </p>

          {createdKey && (
            <div className="p-md bg-secondary-container text-on-secondary-container rounded-lg border border-outline-variant mb-lg font-mono text-[13px] break-all">
              <strong>New Key Created! Copy it now, it will not be shown again:</strong><br/>
              {createdKey}
            </div>
          )}

          <div className="overflow-hidden border border-outline-variant rounded-lg">
            <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left font-body-sm">
              <thead className="bg-surface-container-low text-on-surface-variant border-b border-outline-variant">
                <tr>
                  <th className="px-4 py-3 font-semibold text-[13px]">Name</th>
                  <th className="px-4 py-3 font-semibold text-[13px]">Key (Masked)</th>
                  <th className="px-4 py-3 font-semibold text-[13px]">Expires</th>
                  <th className="px-4 py-3 font-semibold text-[13px]">Created</th>
                  <th className="px-4 py-3 font-semibold text-[13px]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/50">
                {keys.map((k) => (
                  <tr key={k.id} className="hover:bg-surface-container-low/50 transition-colors">
                    <td className="px-4 py-3 text-on-surface font-medium">{k.name}</td>
                    <td className="px-4 py-3 text-on-surface-variant font-mono text-[12px]">{k.key.substring(0, 8)}...{k.key.slice(-4)}</td>
                    <td className="px-4 py-3 text-[12px]">
                      {k.expiresAtMs ? (
                        Date.now() > k.expiresAtMs
                          ? <span className="text-error font-semibold">Expired</span>
                          : <span className="text-on-surface-variant">{new Date(k.expiresAtMs).toLocaleString()}</span>
                      ) : (
                        <span className="text-on-surface-variant">No expiry</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-on-surface-variant text-[12px]">{new Date(k.createdAtMs).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleRevoke(k.id, k.name)}
                        disabled={busy}
                        className="text-error font-label-sm text-[12px] hover:underline disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
                {keys.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-on-surface-variant">No API keys generated yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
        </div>

        {/* Global Store Settings Card */}
        <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg shadow-sm">
          <div className="flex flex-wrap gap-md justify-between items-start mb-lg">
            <div>
              <h3 className="font-headline-md text-[20px] font-semibold text-on-surface">Global Store Settings</h3>
              <p className="font-body-sm text-[13px] text-on-surface-variant mt-1">
                Configure your Apple and Google Play credentials here. These will be used for all apps under this account unless overridden.
              </p>
            </div>
            <button 
              className="bg-primary text-on-primary font-label-md text-[14px] px-lg py-sm rounded-lg hover:opacity-90 disabled:opacity-50 transition-all shadow-sm"
              onClick={handleSaveStoreSettings}
              disabled={busy}
            >
              Save Settings
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-xl">
            {/* Apple Settings */}
            <div className="space-y-md">
              <h4 className="font-label-md text-[14px] font-semibold flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px]">apple</span>
                App Store Server API
              </h4>
              
              <div>
                <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Issuer ID</label>
                <input 
                  type="text" 
                  className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface"
                  placeholder="e.g. 57246542-96fe-1a63-e053-0824d011072a"
                  value={issuerId}
                  onChange={(e) => setIssuerId(e.target.value)}
                />
              </div>

              <div>
                <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Key ID</label>
                <input 
                  type="text" 
                  className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface"
                  placeholder="e.g. 2X9R4HXF34"
                  value={keyId}
                  onChange={(e) => setKeyId(e.target.value)}
                />
              </div>

              <div>
                <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">
                  Private Key (.p8 Content)
                  {hasP8 && <span className="ml-2 text-[11px] text-primary">đã lưu trong Secret Manager — để trống nếu không đổi</span>}
                </label>
                <textarea 
                  className="w-full h-40 bg-[#1e1e1e] border border-outline-variant rounded-lg p-3 text-[#d4d4d4] font-mono text-[11px] resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="-----BEGIN PRIVATE KEY-----\nMIGTAgEAMBMGByqGSM49AgEGCCqGSM49AwEHBHkwdwIBAQQg...\n-----END PRIVATE KEY-----"
                  value={p8Content}
                  onChange={(e) => setP8Content(e.target.value)}
                  spellCheck={false}
                />
              </div>
            </div>

            {/* Google Settings */}
            <div className="space-y-md">
              <h4 className="font-label-md text-[14px] font-semibold flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px]">android</span>
                Google Play Developer API
              </h4>
              
              <div>
                <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">
                  Service Account JSON
                  {hasServiceJson && <span className="ml-2 text-[11px] text-primary">đã lưu trong Secret Manager — để trống nếu không đổi</span>}
                </label>
                <textarea 
                  className="w-full h-[328px] bg-[#1e1e1e] border border-outline-variant rounded-lg p-3 text-[#d4d4d4] font-mono text-[11px] resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Paste your service-account.json content here..."
                  value={serviceJson}
                  onChange={(e) => setServiceJson(e.target.value)}
                  spellCheck={false}
                />
                <p className="font-body-sm text-[11px] text-on-surface-variant mt-2">
                  Ensure the service account has "View financial data" permission in Google Play Console.
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
